import React, { useState, useEffect, useCallback } from "react";
import { IoReceiptOutline } from "react-icons/io5";
import NotificationCard from "../components/NotificationCard";
import ReceiptModal from "../components/ReceiptModal";
import { useData } from "../data/DataProvider";
import { notificationServices } from "../api/notificationServices";

export default function Notifications() {
  const { paymentHistoryData, studentData, activeRouteData, getAllData } =
    useData();
  const [notificationsData, setNotificationsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  useEffect(() => {
    if (typeof getAllData === "function") {
      getAllData();
    }
  }, [getAllData]);

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const notificationsResponse =
          await notificationServices.getNotifications();
        const list =
          notificationsResponse?.data?.data?.notifications ||
          notificationsResponse?.data?.data ||
          [];
        setNotificationsData(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Veri çekilirken hata oluştu:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const handleOpenReceipt = useCallback(
    (item) => {
      const historyRoutes = Array.isArray(paymentHistoryData)
        ? paymentHistoryData
        : [];
      const completedPayments = historyRoutes[0]?.completed_payments || [];

      const payment =
        (item?.payment_id &&
          completedPayments.find((p) => p.id === item.payment_id)) ||
        (item?.installment_number &&
          completedPayments.find(
            (p) => p.installment_number === item.installment_number
          )) ||
        item?.payment ||
        completedPayments[0];

      const studentFullName =
        historyRoutes[0]?.student_name ||
        activeRouteData?.student_name ||
        (studentData?.name && studentData?.surname
          ? `${studentData.name} ${studentData.surname}`
          : studentData?.name || "");

      if (payment) {
        const paidAmt = Number(payment.paid_amount || payment.amount || 0);
        const dateFormatted = payment.paid_at
          ? new Date(payment.paid_at).toLocaleDateString("tr-TR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : payment.created_at
          ? new Date(payment.created_at).toLocaleDateString("tr-TR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-";

        const rawReceiptNo =
          payment.receipt_number ||
          payment.receipt_no ||
          (payment.id || payment.installment_number
            ? `T${String(payment.id || payment.installment_number).padStart(6, "0")}`
            : "-");

        const receiptNo =
          rawReceiptNo === "-"
            ? "-"
            : rawReceiptNo.startsWith("#")
            ? rawReceiptNo
            : `#${rawReceiptNo}`;

        setSelectedReceipt({
          studentName: studentFullName || "-",
          installmentNumber: payment.installment_number || "-",
          date: dateFormatted,
          receiptNo: receiptNo,
          paymentMethod:
            payment.transaction_type ||
            payment.payment_method ||
            "Online Ödeme",
          amount: paidAmt,
          amountFormatted: `₺${paidAmt.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
          receivedBy:
            payment.collector_personnel ||
            payment.received_by ||
            "Sistem",
          status: payment.status === "partial" ? "Kısmi Ödeme" : "Başarılı",
        });
      } else {
        setSelectedReceipt({
          studentName: studentFullName || "-",
          installmentNumber: "-",
          date: "-",
          receiptNo: "-",
          paymentMethod: "-",
          amount: 0,
          amountFormatted: "₺0,00",
          receivedBy: "-",
          status: "-",
        });
      }

      setIsReceiptModalOpen(true);
    },
    [paymentHistoryData, studentData, activeRouteData]
  );

  const handleCloseReceiptModal = useCallback(() => {
    setIsReceiptModalOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-[#f8f9ff] antialiased pb-24 relative">
      <div className="max-w-xl mx-auto px-4 pt-6 space-y-6">
        <div className="pl-1">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Bildirimler
          </h1>
          <p className="text-lg text-gray-500 mt-1 leading-relaxed">
            Ödemeleriniz ve servis durumunuz hakkında güncel kalın.
          </p>
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00306b]"></div>
            </div>
          ) : notificationsData.length > 0 ? (
            notificationsData.map((item, index) => {
              const isPaymentReceipt =
                item.type === "payment_success" ||
                item.type === "payment" ||
                (item.title &&
                  item.title.toLowerCase().includes("ödeme başarı")) ||
                (item.title &&
                  item.title.toLowerCase().includes("odeme basari"));

              const timeFormatted = item.created_at
                ? new Date(item.created_at).toLocaleDateString("tr-TR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : item.time || "";

              return (
                <NotificationCard
                  key={item.id || index}
                  type={item.type}
                  title={item.title}
                  description={item.body || item.description}
                  isRead={item.is_read}
                  time={timeFormatted}
                  linkText={isPaymentReceipt ? "Makbuzu Görüntüle" : undefined}
                  LinkIcon={isPaymentReceipt ? IoReceiptOutline : undefined}
                  onLinkClick={
                    isPaymentReceipt ? () => handleOpenReceipt(item) : undefined
                  }
                />
              );
            })
          ) : (
            <div className="bg-white rounded-2xl p-6 text-center border border-gray-100 shadow-sm">
              <p className="text-gray-600 font-medium">
                Henüz bildiriminiz bulunamadı.
              </p>
            </div>
          )}
        </div>
      </div>

      <ReceiptModal
        visible={isReceiptModalOpen}
        onClose={handleCloseReceiptModal}
        receiptData={selectedReceipt}
      />
    </div>
  );
}
